import strings from 'l10n'
import {
  OverAdvanceChargeFrequency,
  OverAdvanceDaysPerYear,
  OverAdvanceInterestMethod,
  OverAdvanceInterestType,
  OverAdvanceType,
  OverAdvanceZeroBalanceAction,
  OVER_ADVANCE_CHARGE_FREQUENCIES,
  OVER_ADVANCE_DAYS_PER_YEAR,
  OVER_ADVANCE_INTEREST_METHODS,
  OVER_ADVANCE_INTEREST_TYPES,
  OVER_ADVANCE_TYPES,
  OVER_ADVANCE_ZERO_BALANCE_ACTIONS,
} from 'types/overAdvance'

type Option<T extends string> = { label: string; value: T }

const getLabels = (): {
  type: Record<OverAdvanceType, string>
  interestType: Record<OverAdvanceInterestType, string>
  chargeFrequency: Record<OverAdvanceChargeFrequency, string>
  interestMethod: Record<OverAdvanceInterestMethod, string>
  zeroBalanceAction: Record<OverAdvanceZeroBalanceAction, string>
} => ({
  type: {
    FLAT_FEE: strings.FLAT_FEE,
    AMORTIZATION: strings.AMORTIZATION,
    OPEN_BALANCE: strings.OPEN_BALANCE,
  },
  interestType: {
    FLAT_RATE: strings.OA_RATE_TYPE_FIXED,
    PRIME_PLUS: strings.OA_RATE_TYPE_PRIME_PLUS,
    SOFR_PLUS: strings.OA_RATE_TYPE_SOFR_PLUS,
    LIBOR_PLUS: strings.OA_RATE_TYPE_LIBOR_PLUS,
  },
  chargeFrequency: {
    DAILY: strings.DAILY,
    WEEKLY: strings.WEEKLY,
    MONTHLY: strings.MONTHLY,
  },
  interestMethod: {
    SIMPLE: strings.OA_INTEREST_SIMPLE,
    COMPOUND: strings.OA_INTEREST_COMPOUND,
  },
  zeroBalanceAction: {
    CLOSE_AT_ZERO: strings.OA_CLOSE_AT_ZERO,
    STAY_OPEN: strings.OA_STAY_OPEN,
  },
})

const toOptions = <T extends string>(values: readonly T[], labels: Record<T, string>): Option<T>[] =>
  values.map((value) => ({ label: labels[value], value }))

export const overAdvanceTypeLabel = (type: OverAdvanceType) => getLabels().type[type]
export const overAdvanceInterestTypeLabel = (type: OverAdvanceInterestType) => getLabels().interestType[type]
export const overAdvanceChargeFrequencyLabel = (value: OverAdvanceChargeFrequency) =>
  getLabels().chargeFrequency[value]
export const overAdvanceInterestMethodLabel = (value: OverAdvanceInterestMethod) => getLabels().interestMethod[value]
export const overAdvanceZeroBalanceActionLabel = (value: OverAdvanceZeroBalanceAction) =>
  getLabels().zeroBalanceAction[value]

type OpenBalanceGate = { openBalanceEnabled: boolean }

const withoutDisabledTypes = (types: readonly OverAdvanceType[], { openBalanceEnabled }: OpenBalanceGate) =>
  types.filter((type) => openBalanceEnabled || type !== 'OPEN_BALANCE')

export const getOverAdvanceTypeOptions = (gate: OpenBalanceGate) =>
  toOptions(withoutDisabledTypes(OVER_ADVANCE_TYPES, gate), getLabels().type)
export const getOverAdvanceInterestTypeOptions = () => toOptions(OVER_ADVANCE_INTEREST_TYPES, getLabels().interestType)
export const getOverAdvanceChargeFrequencyOptions = () =>
  toOptions(OVER_ADVANCE_CHARGE_FREQUENCIES, getLabels().chargeFrequency)
export const getOverAdvanceInterestMethodOptions = () =>
  toOptions(OVER_ADVANCE_INTEREST_METHODS, getLabels().interestMethod)
export const getOverAdvanceZeroBalanceActionOptions = () =>
  toOptions(OVER_ADVANCE_ZERO_BALANCE_ACTIONS, getLabels().zeroBalanceAction)
export const getOverAdvanceDaysPerYearOptions = () =>
  OVER_ADVANCE_DAYS_PER_YEAR.map((value) => ({ label: String(value), value: String(value) }))

export const OPEN_BALANCE_DEFAULTS = {
  interestType: 'FLAT_RATE' as OverAdvanceInterestType,
  daysPerYear: '360',
  chargeFrequency: 'MONTHLY' as OverAdvanceChargeFrequency,
  interestMethod: 'SIMPLE' as OverAdvanceInterestMethod,
  zeroBalanceAction: 'CLOSE_AT_ZERO' as OverAdvanceZeroBalanceAction,
}

/** Prime / SOFR / LIBOR + types add the entered spread to the factor's base rate. */
export const isSpreadInterestType = (type: OverAdvanceInterestType | undefined | null): boolean =>
  type === 'PRIME_PLUS' || type === 'SOFR_PLUS' || type === 'LIBOR_PLUS'

export const isOverAdvanceDaysPerYear = (value: number): value is OverAdvanceDaysPerYear =>
  (OVER_ADVANCE_DAYS_PER_YEAR as readonly number[]).includes(value)

/** Flat fee has no template. */
export const OVER_ADVANCE_TEMPLATE_TYPES: readonly OverAdvanceType[] = ['AMORTIZATION', 'OPEN_BALANCE']

export const getOverAdvanceTemplateTypeOptions = (gate: OpenBalanceGate) =>
  toOptions(withoutDisabledTypes(OVER_ADVANCE_TEMPLATE_TYPES, gate), getLabels().type)

/** Whether an over advance of this type may be offered given the factor's Open Balance setting. */
export const isOverAdvanceTypeAvailable = (type: OverAdvanceType, gate: OpenBalanceGate) =>
  withoutDisabledTypes([type], gate).length > 0

/** Input limits for Open Balance, mirrored by the API validation. */
export const OPEN_BALANCE_LIMITS = {
  money: { min: 0, minPositive: 0.01, max: 999999999999.99, maxLength: 15, decimals: 2 },
  rate: { min: 0, max: 100, maxLength: 8, decimals: 4 },
} as const

/** At least one non-space character. */
export const NON_BLANK_PATTERN = /\S/
